import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

interface DailyCost {
  date: string;
  displayDate: string;
  cost: number;
  chat_cost?: number;
  research_cost?: number;
  persona_cost?: number;
  conductor_cost?: number;
  summary_cost?: number;
}

interface CostData {
  total_cost_period: number;
  chat_cost_period: number;
  research_cost_period: number;
  persona_cost_period: number;
  conductor_cost_period?: number;
  summary_cost_period?: number;
  daily_costs: DailyCost[];
  time_range: string;
}

interface CostBreakdownProps {
  costData: CostData;
}

const CostBreakdown: React.FC<CostBreakdownProps> = ({ costData }) => {
  // Prepare data for the chart with memoization to prevent unnecessary recalculations
  const chartData = useMemo(() => {
    // Check if we need to sample data (for large datasets)
    const costs = costData.daily_costs || [];
    let dataToProcess = costs;
    
    console.log(`Processing ${costs.length} daily cost records for chart, time range: ${costData.time_range}`);
    
    if (costs.length === 0) {
      console.warn('No daily cost data available for chart');
      return [];
    }
    
    // For large datasets, sample data to improve rendering performance
    if (costs.length > 30) {
      // The backend should already be aggregating data, but this is a fallback
      console.log(`Sampling data points from ${costs.length} to a maximum of 30`);
      
      // If we're dealing with > 30 data points, sample them
      const samplingInterval = Math.ceil(costs.length / 30);
      dataToProcess = costs.filter((_, index) => index % samplingInterval === 0);
    }
    
    // For debugging, log a few sample points
    if (dataToProcess.length > 0) {
      console.log(`First data point: ${JSON.stringify(dataToProcess[0])}`);
      console.log(`Last data point: ${JSON.stringify(dataToProcess[dataToProcess.length - 1])}`);
    }
    
    return dataToProcess.map(day => {
      // For "all time" dates that are ranges like "Jan 01-Jan 07", simplify display
      const name = day.displayDate.includes('-') ? 
        day.displayDate.split('-')[0] + '...' : day.displayDate;
      
      return {
        name,
        fullDate: day.displayDate, // Keep full date for tooltip
        Chat: day.chat_cost || 0,
        Research: day.research_cost || 0,
        Persona: day.persona_cost || 0,
        Conductor: day.conductor_cost || 0,
        Summary: day.summary_cost || 0
      };
    });
  }, [costData.daily_costs, costData.time_range]);

  // Calculate percentages for each category
  const totalCost = costData.total_cost_period || 0.0001; // Avoid division by zero
  const chatPercentage = (costData.chat_cost_period / totalCost * 100) || 0;
  const researchPercentage = (costData.research_cost_period / totalCost * 100) || 0;
  const personaPercentage = (costData.persona_cost_period / totalCost * 100) || 0;
  const conductorPercentage = ((costData.conductor_cost_period || 0) / totalCost * 100) || 0;
  const summaryPercentage = ((costData.summary_cost_period || 0) / totalCost * 100) || 0;

  // Calculate total data points
  const totalDataPoints = costData.daily_costs?.length || 0;

  // Time range display text
  const timeRangeText = () => {
    switch(costData.time_range) {
      case '1d': return 'Last 24 Hours';
      case '7d': return 'Last 7 Days';
      case '30d': return 'Last 30 Days';
      case 'all': return 'All Time';
      default: return costData.time_range;
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h3 className="mb-4 text-lg font-semibold">Cost Analysis ({timeRangeText()})</h3>
      
      <div className="grid grid-cols-1 gap-6 mb-6 md:grid-cols-2">
        {/* Total Cost Card */}
        <div className="p-4 rounded-lg bg-blue-50">
          <h4 className="mb-2 text-md font-semibold text-blue-700">Total API Costs</h4>
          <p className="text-3xl font-bold text-blue-800">${costData.total_cost_period.toFixed(2)}</p>
        </div>
        
        {/* Cost Breakdown Table */}
        <div className="p-4 rounded-lg bg-gray-50">
          <h4 className="mb-2 text-md font-semibold text-gray-700">Cost Breakdown</h4>
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Category</th>
                  <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Cost ($)</th>
                  <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">%</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-blue-600">Chat</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">${(costData.chat_cost_period || 0).toFixed(2)}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">{chatPercentage > 0 ? chatPercentage.toFixed(1) : '0.0'}%</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-green-600">Research</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">${(costData.research_cost_period || 0).toFixed(2)}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">{researchPercentage > 0 ? researchPercentage.toFixed(1) : '0.0'}%</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-purple-600">Persona</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">${(costData.persona_cost_period || 0).toFixed(2)}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">{personaPercentage > 0 ? personaPercentage.toFixed(1) : '0.0'}%</td>
                </tr>
                {costData.conductor_cost_period !== undefined && (
                  <tr>
                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-orange-600">Conductor</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">${(costData.conductor_cost_period || 0).toFixed(2)}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">{conductorPercentage > 0 ? conductorPercentage.toFixed(1) : '0.0'}%</td>
                  </tr>
                )}
                {costData.summary_cost_period !== undefined && (
                  <tr>
                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-indigo-600">Summary</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">${(costData.summary_cost_period || 0).toFixed(2)}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">{summaryPercentage > 0 ? summaryPercentage.toFixed(1) : '0.0'}%</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      {/* Cost Trend Chart */}
      <div className="mt-6">
        <h4 className="mb-2 text-md font-semibold text-gray-700">
          {costData.time_range === 'all' || totalDataPoints > 30 ? 'Cost Trends (Aggregated)' : 'Daily Cost Trends'}
        </h4>
        
        {chartData.length > 0 ? (
          <div className="w-full h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                barSize={chartData.length > 20 ? 10 : 20} // Adjust bar size based on data points
                barGap={0}
                barCategoryGap={chartData.length > 20 ? 5 : 10}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  interval={chartData.length > 20 ? Math.floor(chartData.length / 10) : 0} // Show fewer labels
                  angle={chartData.length > 15 ? -45 : 0} // Angle labels if many data points
                  textAnchor={chartData.length > 15 ? "end" : "middle"}
                  height={chartData.length > 15 ? 60 : 30}
                />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [`$${Number(value).toFixed(2)}`, undefined]}
                  labelFormatter={(label) => {
                    const dataItem = chartData.find(item => item.name === label);
                    return `Date: ${dataItem?.fullDate || label}`;
                  }}
                />
                <Legend />
                <Bar dataKey="Chat" stackId="a" fill="#3B82F6" />
                <Bar dataKey="Research" stackId="a" fill="#10B981" />
                <Bar dataKey="Persona" stackId="a" fill="#8B5CF6" />
                <Bar dataKey="Conductor" stackId="a" fill="#F97316" />
                <Bar dataKey="Summary" stackId="a" fill="#6366F1" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="p-4 text-center bg-gray-50 rounded-md">
            <p className="text-gray-500">No cost data available for the selected time range</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CostBreakdown;